import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray";

/**
 * Copyright (c) 2021 lingdocs.com
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { inflectRegularYeyUnisex } from "./pashto-inflector";
import { baParticle } from "./grammar-units";
import { getBlockRowCol, getPersonInflectionsKey } from "./misc-helpers";
export function concatPsString() {
  for (var _len = arguments.length, items = new Array(_len), _key = 0; _key < _len; _key++) {
    items[_key] = arguments[_key];
  }

  var hasPersonInflections = items.some(function (x) {
    return typeof x !== "string" && "mascSing" in x;
  });

  if (hasPersonInflections) {
    var forceInflection = function forceInflection(arr, inflection) {
      return arr.map(function (element) {
        return typeof element !== "string" && "mascSing" in element ? element[inflection] : element;
      });
    };

    return {
      mascSing: concatPsString.apply(void 0, _toConsumableArray(forceInflection(items, "mascSing"))),
      mascPlur: concatPsString.apply(void 0, _toConsumableArray(forceInflection(items, "mascPlur"))),
      femSing: concatPsString.apply(void 0, _toConsumableArray(forceInflection(items, "femSing"))),
      femPlur: concatPsString.apply(void 0, _toConsumableArray(forceInflection(items, "femPlur")))
    };
  }

  var itemsWOutPersInfs = items;
  var hasLengthOptions = itemsWOutPersInfs.some(function (x) {
    return typeof x !== "string" && "long" in x;
  });

  if (hasLengthOptions) {
    var forceLength = function forceLength(arr, length) {
      return arr.map(function (element) {
        return element !== " " && "long" in element ? element[length] || element.short : element;
      });
    };

    var hasMini = itemsWOutPersInfs.some(function (x) {
      return typeof x !== "string" && "mini" in x;
    });
    return Object.assign(Object.assign({}, hasMini ? {
      mini: concatPsString.apply(void 0, _toConsumableArray(forceLength(items, "mini")))
    } : {}), {
      short: concatPsString.apply(void 0, _toConsumableArray(forceLength(items, "short"))),
      long: concatPsString.apply(void 0, _toConsumableArray(forceLength(items, "long")))
    });
  }

  var itemsWOutLengthOptions = itemsWOutPersInfs;

  var concatField = function concatField(k) {
    return itemsWOutLengthOptions.map(function (item) {
      if (item === " ") return " ";
      if (item === "") return "";
      return item[k];
    }).join("");
  };

  return {
    p: concatField("p"),
    f: concatField("f")
  };
}
export function psFunction(ps, func) {
  return makePsString(func(ps.p), func(ps.f));
}
export function psIncludes(ps, inc) {
  return ps.p.includes(inc.p) && ps.f.includes(inc.f);
}
export function hasBaParticle(ps) {
  return psIncludes(ps, concatPsString(baParticle, " "));
}
export function psRemove(ps, remove) {
  return makePsString(ps.p.replace(remove.p, ""), ps.f.replace(remove.f, ""));
}
export function psInsertWord(ps, toInsert, pos) {
  var pWords = ps.p.split(" ");
  var fWords = ps.f.split(" ");
  var pIns = [].concat(_toConsumableArray(pWords.slice(0, pos)), [toInsert.p], _toConsumableArray(pWords.slice(pos)));
  var fIns = [].concat(_toConsumableArray(fWords.slice(0, pos)), [toInsert.f], _toConsumableArray(fWords.slice(pos)));
  return makePsString(pIns.join(" "), fIns.join(" "));
}
export function ensureBaAt(ps, pos) {
  if ("mascSing" in ps) {
    return {
      mascSing: ensureBaAt(ps.mascSing, pos),
      mascPlur: ensureBaAt(ps.mascPlur, pos),
      femSing: ensureBaAt(ps.femSing, pos),
      femPlur: ensureBaAt(ps.femPlur, pos)
    };
  }

  if ("long" in ps) {
    return Object.assign({
      long: ensureBaAt(ps.long, pos),
      short: ensureBaAt(ps.short, pos)
    }, ps.mini ? {
      mini: ensureBaAt(ps.mini, pos)
    } : {});
  }

  if (!psIncludes(ps, concatPsString(baParticle, " "))) {
    return ps;
  }

  var baRemoved = psRemove(ps, concatPsString(baParticle, " "));
  var baInserted = psInsertWord(baRemoved, baParticle, pos);
  return baInserted;
}
/**
 * Returns the first phonetics value in a comma-seperated list
 *
 * @param f - a phonetics string
 */

export function firstPhonetics(f) {
  return f.split(",")[0];
}
/**
 * returs a PsString or DictionaryEntry ensuring only one phonetics variation
 *
 * @param ps
 */

export function removeFVariants(ps) {
  return Object.assign(Object.assign({}, ps), {
    f: firstPhonetics(ps.f)
  });
}
/**
 * Lets us know if all the forms of a verb block are the same
 *
 * @param block
 */

export function isAllOne(block) {
  return block.reduce(function (isTheSame, row, i, src) {
    return isTheSame && psStringEquals(row[0][0], src[0][0][0]) && psStringEquals(row[1][0], src[1][0][0]);
  }, true);
}
/**
 * Creates a Pashto string structure
 *
 * @param p - the Pashto text
 * @param f - the phonetics text
 */

export function makePsString(p, f) {
  return {
    p: p,
    f: f
  };
}
/**
 * Retuns a Pashto string with the ل - ul on the end removed
 *
 * @param s
 */

export function removeEndingL(s) {
  var lOnEnd = function lOnEnd() {
    var lastPLetter = s.p.slice(-1);
    var lastFLetters = s.f.slice(-2);
    return lastPLetter === "ل" && ["ul", "úl"].includes(lastFLetters);
  };

  if (!lOnEnd()) return s;
  return {
    p: s.p.substr(0, s.p.length - 1),
    f: s.f.substr(0, s.f.length - 2)
  };
}

function getMatchingInflection(infs, persNum, singPlur) {
  return infs[persNum % 2 === 0 ? "masc" : "fem"][singPlur][0];
}

export function isVerbBlock(x) {
  return Array.isArray(x) && x.length === 6 && "p" in x[0][0][0];
}
export function addToForm(toAdd, base, disableLCheck) {
  function startsWithBa(ps) {
    var start = makePsString(ps.p.slice(0, 3), ps.f.slice(0, 3));
    return psStringEquals(start, concatPsString(baParticle, " "));
  }

  function removeBa(ps) {
    return makePsString(ps.p.slice(3), ps.f.slice(3));
  }

  var toAddIncludesObjectMatrix = function toAddIncludesObjectMatrix() {
    return toAdd.some(function (x) {
      return x !== " " && "mascSing" in x;
    });
  };

  function makeNonObjectMatrixForm(base, presObject) {
    function makeLengthOption(length) {
      // If the base is long and there are also length options in toAdd,
      // then make the short and long versions of the base as variations on each item
      var multiplyEachVariationBy = toAdd.reduce(function (acc, cur) {
        return (// make sure we don't make 6 variations when concating a verb block to a verb block!
          Array.isArray(cur) && !isVerbBlock(cur) ? Math.max(acc, cur.length) : acc
        );
      }, 1);
      var b = "long" in base ? base[length] || base.short // in case mini does not exist
      : base;
      var addingLengthChosen = toAdd.map(function (element) {
        if (element !== " " && "long" in element) {
          return element[length] || element.short;
        }

        if (Array.isArray(element)) {
          var arr = element;
          return arr.map(function (e) {
            return "long" in e ? e[length] || e.short : e;
          });
        }

        return element;
      });

      var makeItem = function makeItem(ps, persNum, singPlur, variation, verbBlock) {
        var add = addingLengthChosen.map(function (e) {
          if (e === " ") return e;

          if (isVerbBlock(e)) {
            var block = e;
            return block[persNum][singPlur][0];
          }

          var f = e;

          if (Array.isArray(f)) {
            return f[Math.min(variation, f.length - 1)];
          }

          if ("masc" in f) {
            return getMatchingInflection(f, persNum, singPlur);
          }

          if ("mascSing" in f) {
            return f[presObject ||
            /* istanbul ignore next */
            "mascSing"];
          }

          return f;
        }); // avoid adding the redundant ل on past verb endings
        // TODO: If there's a ba in front, remove it and put it on the front

        return length === "long" && verbBlock && ps.p === "ل" && !disableLCheck ? concatPsString.apply(void 0, _toConsumableArray(add)) : startsWithBa(ps) ? concatPsString.apply(void 0, [baParticle, " "].concat(_toConsumableArray(add), [removeBa(ps)])) : concatPsString.apply(void 0, _toConsumableArray(add).concat([ps]));
      };

      if (b.length === 6) {
        return b.map(function (person, persNum) {
          return person.map(function (item, singPlur) {
            return (// @ts-ignore
              item.reduce(function (vars, ps) {
                var varIndexes = _toConsumableArray(Array(multiplyEachVariationBy).keys());

                return [].concat(_toConsumableArray(vars), _toConsumableArray(varIndexes.map(function (varIndex) {
                  return makeItem(ps, persNum, singPlur, varIndex, true);
                })));
              }, [])
            );
          });
        });
      } // TODO: CHECK IF THE IMPERATIVE BLOCKS WORK??


      return mapImperativeBlock(function (ps, persNumber, singPlur) {
        return makeItem(ps, persNumber, singPlur, 0);
      }, b);
    }

    var useLengthOptions = "long" in base || toAdd.some(function (element) {
      return element !== " " && "long" in element || // @ts-ignore
      Array.isArray(element) && element.some(function (e) {
        return "long" in e;
      });
    });

    if (useLengthOptions) {
      // might be totally unneccessary...
      var miniInToAdd = toAdd.some(function (x) {
        return x !== " " && "mini" in x;
      });
      return Object.assign({
        long: makeLengthOption("long"),
        short: makeLengthOption("short")
      }, "mini" in base || miniInToAdd ? {
        mini: makeLengthOption("mini")
      } : {});
    } // there are no length options in any of the elements or base


    return makeLengthOption("long");
  }

  if (toAddIncludesObjectMatrix() && !("mascSing" in base)) {
    return {
      mascSing: makeNonObjectMatrixForm(base, "mascSing"),
      mascPlur: makeNonObjectMatrixForm(base, "mascPlur"),
      femSing: makeNonObjectMatrixForm(base, "femSing"),
      femPlur: makeNonObjectMatrixForm(base, "femPlur")
    };
  }

  if ("mascSing" in base) {
    return {
      // TODO: Is this really what we want to do?
      // is there ever a case where we would want the object matrix of a compliment
      // to line up with the object matrix of a base verb?
      mascSing: makeNonObjectMatrixForm(base.mascSing, "mascSing"),
      mascPlur: makeNonObjectMatrixForm(base.mascPlur, "mascPlur"),
      femSing: makeNonObjectMatrixForm(base.femSing, "femSing"),
      femPlur: makeNonObjectMatrixForm(base.femPlur, "femPlur")
    };
  }

  return makeNonObjectMatrixForm(base);
}

function mapImperativeBlock(f, block) {
  return block.map(function (person, i) {
    return person.map(function (item, j) {
      return item.map(function (variation) {
        return f(variation, i, j);
      });
    });
  });
}

export function mapVerbBlock(f, block) {
  return block.map(function (person, i) {
    return person.map(function (item, j) {
      return item.map(function (variation) {
        return f(variation, i, j);
      });
    });
  });
}
export function unisexInfToObjectMatrix(inf) {
  return {
    mascSing: inf.masc[0][0],
    mascPlur: inf.masc[1][0],
    femSing: inf.fem[0][0],
    femPlur: inf.fem[1][0]
  };
}
export function concatInflections(comp, infs) {
  var containsLengthOptions = "long" in infs || "long" in comp;

  var ensureL = function ensureL(x, length) {
    return "long" in x ? x[length] : x;
  };

  if (containsLengthOptions) {
    return {
      short: concatInflections(ensureL(comp, "short"), ensureL(infs, "short")),
      long: concatInflections(ensureL(comp, "long"), ensureL(infs, "long"))
    };
  } // now length options are removed


  var complement = comp;
  var infsOneL = infs;

  var mapGender = function mapGender(gender) {
    return infsOneL[gender].map(function (inf, i) {
      return inf.map(function (variation) {
        var c = "masc" in complement ? complement[gender][i][0] : complement;
        return concatPsString(c, " ", variation);
      });
    });
  };

  return {
    masc: mapGender("masc"),
    fem: mapGender("fem")
  };
}
/**
 * Checks if a given infinitive ends in یل - yul such as وایل - waayul etc.
 *
 * @param s
 */

export function yulEndingInfinitive(s) {
  var pEnding = s.p.slice(-2);
  var fEnding = s.f.slice(-3);
  return pEnding === "یل" && ["yul", "yúl"].includes(fEnding);
}
export function psStringFromEntry(entry) {
  return makePsString(entry.p, firstPhonetics(entry.f));
}
export function allOnePersonInflection(block, person) {
  if ("mascSing" in block) {
    var key = getPersonInflectionsKey(person);
    return block[key];
  }

  return block;
}
export function choosePersInf(x, persInf) {
  if ("mascSing" in x) {
    return x[persInf];
  }

  return x;
}
export function allOnePersonVerbForm(block, person) {
  if ("mascSing" in block) {
    return {
      mascSing: allOnePersonVerbForm(block.mascSing, person),
      mascPlur: allOnePersonVerbForm(block.mascPlur, person),
      femSing: allOnePersonVerbForm(block.femSing, person),
      femPlur: allOnePersonVerbForm(block.femPlur, person)
    };
  }

  if ("long" in block) {
    return Object.assign({
      long: allOnePersonVerbForm(block.long, person),
      short: allOnePersonVerbForm(block.short, person)
    }, block.mini ? {
      mini: allOnePersonVerbForm(block.mini, person)
    } : {});
  }

  var _getBlockRowCol = getBlockRowCol(person),
      _getBlockRowCol2 = _slicedToArray(_getBlockRowCol, 2),
      row = _getBlockRowCol2[0],
      col = _getBlockRowCol2[1];

  var p = block[row][col];
  return [[p, p], [p, p], [p, p], [p, p], [p, p], [p, p]];
}
/**
 * Returns a set of inflections that are all masculine plural
 * (for conjugating the past participle of gramatically transitive verbs)
 *
 * @param inflections
 */

export function allMascFirstInflection(inflections) {
  if ("long" in inflections) {
    return {
      long: allMascFirstInflection(inflections.long),
      short: allMascFirstInflection(inflections.short)
    };
  }

  var mp = inflections.masc[1];
  return {
    masc: [mp, mp, mp],
    fem: [mp, mp, mp]
  };
}
export function complementInflects(inf) {
  return inf.masc[0][0].p !== inf.masc[2][0].p || inf.fem[0][0].p !== inf.fem[1][0].p || inf.masc[0][0].p !== inf.fem[0][0].p; // OR MORE THOROUGH?
  // const fm = inf.masc[0][0];
  // return !(
  //     psStringEquals(inf.masc[1][0], fm) &&
  //     psStringEquals(inf.masc[2][0], fm) &&
  //     psStringEquals(inf.fem[1][0], fm) &&
  //     psStringEquals(inf.fem[2][0], fm)
  // );
}
export function psStringEquals(ps1, ps2) {
  return ps1.p === ps2.p && ps1.f === ps2.f;
}
export function removeRetroflexR(ps) {
  return {
    p: ps.p.replace("ړ", ""),
    f: ps.f.replace("R", "")
  };
}
export function inflectYey(ps) {
  if ("long" in ps) {
    return {
      long: inflectYey(ps.long),
      short: inflectYey(ps.short)
    };
  }

  return inflectRegularYeyUnisex(ps.p, ps.f);
}
export function clamp(s, chars) {
  return "".concat(s.slice(0, 20)).concat(s.length > 20 ? "..." : "");
}
export function addEnglish(english, ps) {
  if (Array.isArray(ps[0]) && ps.length === 6) {
    return mapVerbBlock(function (psString, i, j) {
      return Object.assign(Object.assign({}, psString), {
        // @ts-ignore
        e: typeof english === "string" ? english : english[i][j]
      });
    }, ps);
  }

  if (Array.isArray(ps[0]) && ps.length === 2) {
    return mapImperativeBlock(function (psString, i, j) {
      return Object.assign(Object.assign({}, psString), {
        // @ts-ignore
        e: typeof english === "string" ? english : english[i][j]
      });
    }, ps);
  }

  var line = ps;
  return line.map(function (psString) {
    return Object.assign(Object.assign({}, psString), {
      e: typeof english === "string" ? english : english[0][0]
    });
  });
}
export function beginsWithDirectionalPronoun(ps) {
  var beginning = ps.p.slice(0, 2);
  return ["را", "در", "ور"].includes(beginning);
}
export function checkForOoPrefix(ps) {
  return ps.p[0] === "و" && ["oo", "óo"].includes(ps.f.slice(0, 2));
}
export function startsWithBa(ps) {
  return ps.p.slice(0, 3) === "به " && ps.f.slice(0, 3) === "ba ";
}
/**
 * Removes a given head from a verb form, returning just the second half of the split
 * It keeps به in front if there is a به at the beginning of the form
 *
 * @param head - the first part of a verb split
 * @param ps - the whole verb form that needs the head removed
 */

export function removeHead(head, ps) {
  var hasBa = startsWithBa(ps);
  var base = hasBa ? psRemove(ps, concatPsString(baParticle, " ")) : ps;
  var chopped = {
    p: base.p.slice(head.p.length),
    f: base.f.slice(head.f.length)
  };
  return hasBa ? concatPsString(baParticle, " ", chopped) : chopped;
}
export function uniquePsStringArray(arr) {
  return _toConsumableArray(new Set(arr.map(function (o) {
    return JSON.stringify(o);
  }))).map(function (string) {
    return JSON.parse(string);
  });
}
export function splitOffLeapfrogWord(ps) {
  var pWords = ps.p.split(" ");
  var fWords = ps.f.split(" ");
  var beginning = makePsString(pWords.slice(0, -1).join(" "), fWords.slice(0, -1).join(" "));
  var end = makePsString(pWords.slice(-1).join(" "), fWords.slice(-1).join(" "));
  return [beginning, end];
}
export function removeObjComp(comp, ps) {
  if (!comp) {
    return ps;
  }

  return makePsString(ps.p.replace(comp.p + " ", ""), ps.f.replace(comp.f + " ", ""));
}
export function psStringContains(ps, searchFor) {
  return ps.p.includes(searchFor.p) && ps.f.includes(searchFor.f);
}
export function removeStartingTick(f) {
  if (f[0] === "`") {
    return f.slice(1);
  }

  return f;
}
export function ensureShortWurShwaShift(ps) {
  if (ps.p.slice(-2) === "وړ" && ps.f.slice(-2) === "wR") {
    return {
      p: ps.p,
      f: ps.f.slice(0, -2) + "wuR"
    };
  }

  return ps;
}